import {
  TripSearchFlattenedOptions,
  Trip,
  TripDetailsModel,
  TripBillingStatus,
  TripDetailsUpdate,
  TripHeaderUpdate,
  OrderLogRequest,
  OrderLog,
  B2BActivityRequest,
  B2BActivityResult,
  TripB2BError,
  TripModification,
} from "trips/models";
import { PagedResults } from "common/models";
import apiClient from "./apiClient";
import { toast } from "react-toastify";

const searchTripNumber = async (tripNumber: string): Promise<string[]> => {
  return apiClient.get(`/trips/search/${tripNumber}`);
};

const searchTripIdentifier = async (tripNumber: string, identifierType?: string): Promise<string[]> => {
  return apiClient.get(`/trips/search-identifier/${tripNumber}/${identifierType}`);
};

const getTrips = async (options: TripSearchFlattenedOptions): Promise<PagedResults<Trip>> => {
  return apiClient.get(`/trips`, options);
};

const getTrip = async (tripNumber: string): Promise<TripDetailsModel> => {
  return apiClient.get(`/trips/${tripNumber}`);
};

const getTripBillingStatus = async (tripNumber: string): Promise<TripBillingStatus> => {
  return apiClient.get(`/trips/${tripNumber}/billingStatus`);
};

const updateTripBillingStatus = async (controlNumber: string, tripNumber: number): Promise<void> => {
  return apiClient.post(`/trips/controlNumber/${controlNumber}/updatebillingStatus`, { tripNumber });
};

const updateDetails = async (trip: TripDetailsUpdate): Promise<any> => {
  return apiClient
    .put(`/trips/details/${trip.id}`, trip)
    .then((t) => toast.success("Trip Details Updated."))
    .catch((e) => toast.error("Trip Update Failed"));
};

const updateHeader = async (trip: TripHeaderUpdate): Promise<any> => {
  return apiClient
    .put(`/trips/header/${trip.id}`, trip)
    .then((t) => toast.success("Trip Header Updated."))
    .catch((e) => toast.error("Trip Header Update Failed"));
};

const accept = async (id: number): Promise<any> => {
  return apiClient
    .post(`/trips/accept/${id}`, {})
    .then((t) => toast.success("Trip Accepted."))
    .catch((e) => toast.error("Trip Accept Failed"));
};

const finalize = async (id: number): Promise<any> => {
  return apiClient
    .post(`/trips/finalize/${id}`, {});
};

const bulkApprove = async (request: { tripIds?: number[], searchFilters?: any }): Promise<any> => {
  return apiClient.post(`/trips/bulk-approve`, request);
};

const cancel = async (id: number, reasonId: number): Promise<any> => {
  return apiClient
    .post(`/trips/cancel/${id}`, { id: id, reasonId: reasonId })
    .then((t) => toast.success("Trip Canceled."))
    .catch((e) => toast.error("Trip Cancel Failed"));
};

const decline = async (id: number, reasonId: number): Promise<any> => {
  return apiClient
    .post(`/trips/decline/${id}`, { id: id, reasonId: reasonId })
    .then((t) => toast.success("Trip Declined."))
    .catch((e) => toast.error("Trip Decline Failed"));
};

const split = async (id: number): Promise<any> => {
  return apiClient
    .post(`/trips/split/${id}`, {})
    .then((t) => toast.success("Trip Split."))
    .catch((e) => toast.error("Trip Split Failed"));
};
const clone = async (id: number): Promise<any> => {
  return apiClient
    .post(`/trips/clone/${id}`, {})
    .then((t) => toast.success("Trip Cloned."))
    .catch((e) => toast.error("Trip Clone Failed"));
};
const getOrderLogs = async (request: OrderLogRequest): Promise<PagedResults<OrderLog>> => {
  return apiClient.post(`/trips/${request.tripId}/order-logs`, request);
};

const getB2BActivity = async (request: B2BActivityRequest): Promise<PagedResults<B2BActivityResult>> => {
  return apiClient.post(`/trips/${request.tripId}/b2b-activity`, request);
};

/**
 * The railroad's outstanding rejection for a trip, or null when there is none.
 *
 * Susan, 2026-09-25: a trip kicked back for a B2B error showed no reason in Flow, so the closer
 * had to open Dashboard's Trip Event Admin to find out what to fix. The BFF answers 204 for the
 * (very common) no-error case, which apiClient surfaces as an empty body.
 */
const getB2BError = async (tripId: number | string): Promise<TripB2BError | null> => {
  const result = await apiClient.get(`/trips/${tripId}/b2b-error`);
  return result && (result as TripB2BError).b2BErrorMessage ? (result as TripB2BError) : null;
};

const getModifications = async (tripId: number | string): Promise<TripModification[]> => {
  return apiClient.get(`/trips/${tripId}/modifications`);
};

const deleteTripParent = async (id): Promise<any> => {
  return apiClient
    .remove(`/trips/${id}/parent`, {})
    .then((t) => toast.success("Trip Parent Deleted."))
    .catch((e) => toast.error("Trip Parent Delete Failed"));
};

const validate = async (id): Promise<any> => {
  return apiClient.get(`/trips/${id}/validate`);
};

const overrideFinalize = async (id: number, overrideReason: string): Promise<any> => {
  return apiClient.post(`/trips/finalize/${id}/override`, {
    overrideReason
  });
};

const reopen = async (id: number): Promise<any> => {
  return apiClient.post(`/trips/reopen/${id}`, {});
};

const verify = async (id: number, verified: boolean): Promise<any> => {
  return apiClient.post(`/trips/verify/${id}`, { verified });
};

const setManagerReview = async (id: number, mapped: boolean): Promise<any> => {
  return apiClient.post(`/trips/manager-review/${id}`, { mapped });
};

const tripService = {
  getB2BError,
  getTripBillingStatus,
  searchTripNumber,
  searchTripIdentifier,
  getTrips,
  getTrip,
  getOrderLogs,
  getB2BActivity,
  getModifications,
  updateTripBillingStatus,
  updateHeader,
  updateDetails,
  accept,
  finalize,
  bulkApprove,
  cancel,
  decline,
  split,
  clone,
  deleteTripParent,
  validate,
  overrideFinalize,
  reopen,
  verify,
  setManagerReview,
};

export default tripService;
